import type { RequestPayload } from "@inertiajs/core";
import { router, usePage } from "@inertiajs/react";
import { Checkbox, Divider, Group as MantineGroup, NumberInput, Radio } from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import type { ReactNode } from "react";
import { notEmptyMessage } from "../../../helpers/formHelper";
import type { Occupation } from "../../../models/enums/occupation";
import { ReportType } from "../../../models/enums/reportType";
import { Role } from "../../../models/enums/role";
import type { Group } from "../../../models/group";
import type { EducationConfiguration } from "../../../models/reportConfigs/educationConfiguration";
import type { YearContext } from "../../../models/yearContext";
import { AccessConfigForm, type AccessConfigFormType } from "../../accessConfig/AccessConfigForm";
import { SaveButton } from "../../button/SaveButton";
import { emailTemplateValidation } from "../../email/EmailEditorForm";
import { BaseConfigForm, type BaseConfigFormType } from "./BaseConfigForm";

type EducationConfigFormProps = {
  educationConfig?: EducationConfiguration;
  saveButtonLabel: string;
  yearContexts: YearContext[];
  groups: Group[];
  onSubmit: (values: RequestPayload) => void;
  semester: "1" | "2";
  alert?: ReactNode;
};

type EducationFormType = AccessConfigFormType &
  BaseConfigFormType & {
    report_type: ReportType;
    data: {
      semester: "1" | "2";
      enable_point_eight?: boolean;
      max_remark_chars?: number | null;
    };
  };

type FormErrors = {
  title?: string[];
  data?: string[];
  report_type?: string[];
};

export function EducationConfigForm({
  educationConfig,
  saveButtonLabel,
  yearContexts,
  groups,
  onSubmit,
  semester,
  alert,
}: EducationConfigFormProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;

  const form = useForm<EducationFormType>({
    mode: "uncontrolled",
    initialValues: {
      report_type: educationConfig?.report_type ?? (semester === "1" ? ReportType.EDUC1 : ReportType.EDUC2),
      title: educationConfig?.title,
      group_id: educationConfig?.group?.id.toString(),
      year_context_id: educationConfig?.year_context?.id.toString(),
      occupation: educationConfig?.occupation,
      document_readable: educationConfig?.document_readable ?? [Role.APPRENTICE, Role.APPRENTICE_SUPERVISOR, Role.PARENT],
      document_signable: educationConfig?.document_signable ?? [Role.APPRENTICE, Role.PARENT],
      email_template: educationConfig?.email_template,
      send_mail: educationConfig?.send_mail ?? true,
      subject: educationConfig?.subject,
      data: {
        semester: educationConfig?.data.semester ?? semester,
        enable_point_eight: educationConfig?.data.enable_point_eight ?? semester === "2",
        max_remark_chars: educationConfig?.data.max_remark_chars ?? null,
      },
    },
    validate: {
      title: isNotEmpty(notEmptyMessage),
      subject: (value, values) => (values.send_mail ? isNotEmpty(notEmptyMessage)(value) : null),
      email_template: (value, values) => (values.send_mail ? emailTemplateValidation(value) : null),
      data: {
        max_remark_chars: isNotEmpty(notEmptyMessage),
      },
    },
  });

  form.watch("year_context_id", ({ value }) => reloadGroups(value, form.getValues().occupation));
  form.watch("occupation", ({ value }) => reloadGroups(form.getValues().year_context_id, value));

  const reloadGroups = (yearContextId?: string, occupation?: Occupation) =>
    router.reload({
      only: ["groups"],
      data: { selected_year_context_id: yearContextId, selected_occupation: occupation },
    });

  return (
    <BaseConfigForm
      form={form}
      groups={groups}
      yearContexts={yearContexts}
      errors={errors}
      alert={alert}
      saveButtonLabel={saveButtonLabel}
      onSubmit={onSubmit}
    >
      <Radio.Group label="Semester" key={form.key("data.semester")} {...form.getInputProps("data.semester")}>
        <MantineGroup gap="xl">
          <Radio disabled value="1" label="1" size="lg" />
          <Radio disabled value="2" label="2" size="lg" />
        </MantineGroup>
      </Radio.Group>
      <NumberInput
        label="Maximale Anzahl an Zeichen pro Bemerkungsfeld"
        key={form.key("data.max_remark_chars")}
        {...form.getInputProps("data.max_remark_chars")}
        withAsterisk
      />
      <Checkbox
        label="Mit Punkt 8 (Überprüfen der Zielerreichung im abgelaufenen Semester)"
        key={form.key("data.enable_point_eight")}
        {...form.getInputProps("data.enable_point_eight", { type: "checkbox" })}
      />
      <AccessConfigForm form={form} />
      <Divider />
      <SaveButton label={saveButtonLabel} />
    </BaseConfigForm>
  );
}
